import { ReactNode, useEffect, useId } from "react";
import FocusLock from "react-focus-lock";
import { cn } from "@/lib/cn";
import { X } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export function Dialog({ open, onClose, title, description, children, className }: Props) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <FocusLock returnFocus>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          className={cn(
            "relative w-full max-w-md glass-elevated shadow-2xl",
            className,
          )}
        >
          {(title || description) && (
            <div className="card-header flex items-start justify-between gap-4">
              <div>
                {title && (
                  <h2 id={titleId} className="font-display text-lg font-semibold">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="text-sm text-fg-muted mt-1">{description}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-fg-subtle hover:text-fg p-1 -m-1"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          )}
          <div className="card-body">{children}</div>
        </div>
      </FocusLock>
    </div>
  );
}
